import Modal from "../../../components/Modal";
import { useTranslation } from "react-i18next";
import AddCompany from "../../Inventory/components/AddCompany";
import AddPlaceOfBusiness from "../../Inventory/components/AddPlaceOfBusiness";
import {
  Divider,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  VStack
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { getAxiosInstance } from "../../../utils/axiosInstance";
import { toast } from "react-toastify";
import SearchProductInInventory from "../../../components/Modal/SearchProductInInventory";
import { BiTrash } from "react-icons/bi";
import AddUnrestrictedCompany from "../../Inventory/components/AddUnrestrictedCompany";
import AddUnrestrictedPlaceOfBusiness from "../../Inventory/components/AddUnrestrictedPlaceOfBusiness";
import SearchProductInInventoryAfterName from "../../../components/Modal/SearchProductInInventoryAfterName";
import ScanProductSerialNumbers from "../../Inventory/components/ScanProductSerialNumbers";
import ItemsWithNoPriceNotification from "./ItemsWithNoPriceNotification";
import itemsWithNoPriceNotification from "./ItemsWithNoPriceNotification";
import SearchProductInInventoryAfterSKU from "../../../components/Modal/SearchProductInInventoryAfterSKU";

interface CreateTransfer {
  open: boolean;
  onClose: () => void;
}

const CreateTransfer = (props: CreateTransfer) => {
  const {
    open,
    onClose
  } = props;

  const { t } = useTranslation("translations");
  const [products, setProducts] = useState<any[]>([]);

  const [company, setCompany] = useState<string>("");
  const [placeOfBusiness, setPlaceOfBusiness] = useState<string>("");

  const [toCompany, setToCompany] = useState<string>("");
  const [toPlaceOfBusiness, setToPlaceOfBusiness] = useState<string>("");

  const [openItemsWithNoPriceNotification, setOpenItemsWithNoPriceNotification] = useState(false);

  const deleteProduct = (index: number) => {
    const tmp = [...products];
    tmp.splice(index, 1);
    setProducts(tmp);
  };

  const onEditProductQuantity = (index: number, newQuantity: number) => {
    const tmp = [...products];
    tmp[index].transferQuantity = Math.min(tmp[index].quantity, newQuantity);

    // update the size of the serial numbers array if the product has serial numbers 
    if (tmp[index].hasSerialNumber && tmp[index].serialNumbers?.length !== tmp[index].transferQuantity) {
      const diff = tmp[index].transferQuantity - tmp[index].serialNumbers.length;

      if(diff >= 1) {
        tmp[index].serialNumbers = [...tmp[index].serialNumbers, ...new Array(diff).fill("")];
      } else {
        tmp[index].serialNumbers = tmp[index].serialNumbers.splice(tmp[index].serialNumbers.length + diff, diff) 
      }
    }

    setProducts(tmp);
  };

  const onEditProductSerialNumbers = (arr: string[], index: number) => {
    const tmp = [...products];
    tmp[index].serialNumbers = arr;
    setProducts(tmp);
  }

  const validateSerialNumber = async (id: string, productId: string, serialNumber: string): Promise<boolean> => {
    try {
      const res = await getAxiosInstance().get(`Inventory/PlaceOfBusiness/${id}/${productId}/${serialNumber}`);
      return res.data;
    } catch (e) {
      console.log(e);
      return false;
    }
  }

  const makeTransfer = async () => {
    // If we have products that don't have a selling price will display a notification and return early
    if (products.findIndex(x => !x.hasPriceSet) !== -1)  {
      setOpenItemsWithNoPriceNotification(true);
      return;
    }

    if(products.length < 1) return;

    try {
      const fData = products.map((x) => ({
        fromId: placeOfBusiness,
        toId: toPlaceOfBusiness,
        productId: x.productId,
        quantity: x.transferQuantity,
        serialNumbers: x.serialNumbers
      }));

      await getAxiosInstance().post("/Transfer", fData);

      toast.success(t("TRANSFER_CREATED"), { position: "bottom-right"})
      onClose();
    } catch (e) {
      toast.error(t("ERROR_CREATING_THE_TRANSFER"), { position: "bottom-right"})
    }
  }

  return <Modal
    header={t("CREATE_TRANSFER_MODAL")}
    isOpen={open}
    onClose={onClose}
    secondaryActionName={t("CREATE_TRANSFER_ACTION")}
    secondaryActionEnabled={true}
    secondaryAction={() => {
      makeTransfer();
    }}
    size="md"
  >
    <>
      <Text fontWeight="semibold" className="mt-4 mb-2 text-xl">{t("TRANSFER_FROM")}</Text>
      <Divider className="mb-2" />

      <AddCompany
        selectedCompany={company}
        setSelectedCompany={setCompany}
      />
      <AddPlaceOfBusiness
        companyId={company}
        selectedPlaceOfBusiness={placeOfBusiness}
        setSelectedPlaceOfBusiness={setPlaceOfBusiness}
      />

      <Text fontWeight="semibold" className="mt-4 mb-2 text-xl">{t("TRANSFER_TO")}</Text>
      <Divider className="mb-2" />

      <AddUnrestrictedCompany
        selectedCompany={toCompany}
        setSelectedCompany={setToCompany}
      />
      <AddUnrestrictedPlaceOfBusiness
        companyId={toCompany}
        selectedPlaceOfBusiness={toPlaceOfBusiness}
        setSelectedPlaceOfBusiness={setToPlaceOfBusiness}
      />

      <SearchProductInInventory
        addProduct={(data) => {
          setProducts([
            ...products,
            {
              ...data,
              transferQuantity: 0,
              serialNumbers: []
            }
          ])
        }
        }
        placeOfBusinessId={placeOfBusiness}
      />

      <SearchProductInInventoryAfterName
        addProduct={(data) => {
          setProducts([
            ...products,
            {
              ...data,
              transferQuantity: 0,
              serialNumbers: []
            }
          ])
        }}
        placeOfBusinessId={placeOfBusiness}
      />

      <SearchProductInInventoryAfterSKU
          addProduct={(data) => {
            setProducts([
              ...products,
              {
                ...data,
                transferQuantity: 0,
                serialNumbers: []
              }
            ])
          }}
          placeOfBusinessId={placeOfBusiness}
      />

      <VStack align="start">
        <Text fontWeight="semibold" className="mt-4" size="md">{t("TRANSFER_PRODUCT")}</Text>
        <Divider />
      </VStack>
      {
        products.map((p, index) => <div key={`scan_serial_number_${index}`}>
          <div className="font-bold text-xl my-2">
            {p.productName} ({t("MAX_QUANTITY")}: {p.quantity})
          </div>
          <div className="flex flex-row items-center">
            <IconButton
              colorScheme='red'
              aria-label='Remove element'
              fontSize={25}
              icon={<BiTrash />}
              mr={2}
              onClick={() => deleteProduct(index)}
            />
            <InputGroup>
              <Input
                placeholder={t("TRANSFER_QUANTITY")}
                type="number"
                inputMode="numeric"
                value={p.transferQuantity}
                onChange={(e) => onEditProductQuantity(index, Math.max(+e.target.value, 0))}
              />
              <InputRightElement className="text-blue-500 mr-6">
                ({p.um})
              </InputRightElement>
            </InputGroup>
          </div>
          {
            p.hasSerialNumber &&
            <ScanProductSerialNumbers
              serialNumbers={p.serialNumbers}
              onSerialNumbersChange={(arr: string[]) => onEditProductSerialNumbers(arr, index)}
              validateSerialNumber={true}
              validateFunction={async (serial) => await validateSerialNumber(placeOfBusiness, p.productId, serial)}
            />
          }
        </div>)
      }
      <ItemsWithNoPriceNotification
          items={products.filter(x => !x.hasPriceSet).map(x => ({ name: x.productName, id: x.productId }))}
          isOpen={openItemsWithNoPriceNotification}
          onClose={() => {
            setOpenItemsWithNoPriceNotification(false);
            onClose();
          }}
      />
    </>
  </Modal>
}

export default CreateTransfer;